import { Spacer } from "@cd2/components/build/es6"
import React, { useEffect } from "react"
import ReactDOM from "react-dom"
import { useManagedConfirmation } from "./useManagedConfirmation"
import { Fields } from "@cd2/forms/build/es6"
import { ConfirmCancelButtons } from "../interfaceParts/ConfirmCancelButtons"
import { Dialogue } from "../interfaceParts/Dialogue"
import { useDialogueConfirmation2 } from "./useDialogConfirmation"
import { TextGroupMedium } from "../interfaceParts/Title"

interface IQuickConfirmationData {
  heading?: React.ReactNode
  body?: React.ReactNode
  yesText?: string
  noText?: string
  requireCheckbox?: boolean
}

function usePortalNode() {
  const node = React.useRef(document.createElement("div"))

  useEffect(() => {
    node.current.style.position = "relative"
    node.current.style.zIndex = "1000"
    document.body.appendChild(node.current)
    return () => {
      document.body.removeChild(node.current)
    }
  })

  return node.current
}

export function useQuickConfirmationModal() {
  const portalNode = usePortalNode()
  const [openModal, { showDialog, onResolve, data }] = useManagedConfirmation<
    IQuickConfirmationData,
    boolean | null
  >({
    onShow() {
      setRequiredValue(false)
    },
  })
  const [requiredValue, setRequiredValue] = React.useState(false)
  let node = (
    <Dialogue
      width={500}
      onRequestClose={() => onResolve(false)}
      isOpen={showDialog}
      heading={data?.heading ?? "Are you sure?"}
      subheading={data?.body ?? "Are you sure you wish to perform this action?"}
      noCloseButton
    >
      {data?.requireCheckbox && (
        <React.Fragment>
          <Fields.Checkbox
            label="This action cannot be undone, are you sure?"
            value={requiredValue}
            onChange={setRequiredValue}
          />
          <Spacer size={16} />
        </React.Fragment>
      )}
      <ConfirmCancelButtons
        onConfirm={() => onResolve(true)}
        onCancel={() => onResolve(false)}
        buttonOpts={{
          confirm: {
            children: data?.yesText ?? "Yes",
            disabled: data?.requireCheckbox && !requiredValue,
          },
          cancel: { children: data?.noText ?? "Cancel" },
        }}
      />
    </Dialogue>
  )

  node = ReactDOM.createPortal(node, portalNode)

  return [openModal, node] as const
}
export function useQuickConfirmationModal2() {
  return useDialogueConfirmation2<boolean, IQuickConfirmationData>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))
      const [requiredValue, setRequiredValue] = React.useState(false)

      return (
        <div>
          <TextGroupMedium
            heading={dialogData.heading ?? "Are you sure?"}
            body={dialogData.body ?? "Are you sure you wish to perform this action?"}
          />
          <Spacer />
          {dialogData.requireCheckbox && (
            <React.Fragment>
              <Fields.Checkbox
                label="This action cannot be undone, are you sure?"
                value={requiredValue}
                onChange={setRequiredValue}
              />
              <Spacer size={16} />
            </React.Fragment>
          )}
          <ConfirmCancelButtons
            onConfirm={() => onResolve(true)}
            onCancel={() => onResolve(false)}
            buttonOpts={{
              confirm: {
                children: dialogData.yesText ?? "Yes",
                disabled: dialogData.requireCheckbox && !requiredValue,
              },
              cancel: { children: dialogData.noText ?? "Cancel" },
            }}
          />
        </div>
      )
    },
  })
}
